import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Building2, Plus, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CampusManagement() {
  const [campuses, setCampuses] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/campuses", {
        params: { include_archived: includeArchived },
      });
      setCampuses(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/campuses", { name, address });
      setName("");
      setAddress("");
      toast.success("Campus added.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this campus?")) return;
    try {
      await api.post(`/campuses/${id}/archive`);
      toast.success("Campus archived.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/campuses/${c.id}`, { active: !c.active });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="campus-management-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Super Admin
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">Campuses</h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          The five Patria homes across California. Add new campuses as we grow, archive
          rather than delete.
        </p>
      </div>

      <form onSubmit={create} className="patria-card p-5 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Campus name
          </label>
          <input
            className="patria-input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Ontario"
            required
            data-testid="campus-name-input"
          />
        </div>
        <div className="sm:col-span-3">
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Address
          </label>
          <input
            className="patria-input w-full"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g., Ontario, CA"
            data-testid="campus-address-input"
          />
        </div>
        <button type="submit" disabled={saving} className="patria-btn-primary" data-testid="campus-add-btn">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add campus
        </button>
      </form>

      <label className="flex items-center gap-2 text-sm text-charcoal-soft">
        <input
          type="checkbox"
          className="rounded"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
          data-testid="campus-include-archived"
        />
        Show archived
      </label>

      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Campus</th>
              <th>Address</th>
              <th>Active</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campuses.length === 0 && (
              <tr>
                <td colSpan={4} className="italic text-charcoal-muted">
                  No campuses to show.
                </td>
              </tr>
            )}
            {campuses.map((c) => (
              <tr key={c.id} className={c.archived ? "opacity-70" : ""} data-testid={`campus-row-${c.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 rounded-full bg-forest-600/10 text-forest-700 items-center justify-center">
                      <Building2 className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <span className="font-medium text-forest-900">
                      {c.name}
                      {c.archived && (
                        <span className="ml-2 patria-chip-terra">Archived</span>
                      )}
                    </span>
                  </div>
                </td>
                <td>{c.address || "—"}</td>
                <td>
                  <button
                    className={c.active ? "patria-chip-green" : "patria-chip-terra"}
                    onClick={() => toggleActive(c)}
                    data-testid={`campus-active-toggle-${c.id}`}
                  >
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="text-right">
                  {!c.archived && (
                    <button
                      className="patria-btn-ghost text-terracotta-600"
                      onClick={() => archive(c.id)}
                      data-testid={`campus-archive-${c.id}`}
                    >
                      <Archive className="h-4 w-4" strokeWidth={1.5} /> Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
